(() => {
  if (customElements.get("fd-article-content")) return;

  const FINE_QUERY = "(hover: hover) and (pointer: fine)";
  const OPEN_DELAY = 180;
  const CLOSE_DELAY = 300;
  const GAP = 8;
  const EDGE = 16;
  const MIN_CLAMP = 140;
  const BLOCK_SELECTOR =
    "p, blockquote, h1, h2, h3, h4, h5, h6, figure, td, li";

  const nameKey = (value) =>
    String(value || "")
      .normalize("NFKC")
      .toLowerCase()
      .replace(/&#39;/g, "'")
      .replace(/[‘’ʼ`]/g, "'")
      .replace(/[“”™®"]/g, "")
      .replace(/'/g, "")
      .replace(/[^a-z0-9+]+/g, " ")
      .trim()
      .replace(/\bwomen\b/g, "womens")
      .replace(/\bmen\b/g, "mens")
      .replace(/\bkid\b/g, "kids")
      .replace(/\bteen\b/g, "teens")
      .replace(/\btoddler\b/g, "toddlers")
      .replace(/^(first day s|first days|first day|our|the) /, "")
      .replace(/^(first day s|first days|first day) /, "")
      .trim();

  const stripTail = (key) =>
    key.replace(
      / (gummies|gummy|multivitamins|vitamins|supplements|supplement)$/,
      "",
    );

  const namesFor = (tpl) =>
    [
      tpl.dataset.productTitle,
      ...String(tpl.dataset.productNames || "").split("|"),
    ]
      .map(nameKey)
      .filter((key) => key.split(" ").length >= 2);

  const isMention = (text, names, matchCta) => {
    const key = nameKey(text);
    if (!key) return false;
    if (names.includes(key) || names.includes(stripTail(key))) return true;
    return matchCta && names.some((name) => ` ${key} `.includes(` ${name} `));
  };

  const isBlank = (value) =>
    !String(value || "")
      .replace(/ /g, " ")
      .trim();

  const slugify = (value) =>
    String(value || "")
      .toLowerCase()
      .replace(/['"()[\]]/g, "")
      .replace(/[^a-z0-9_]+/g, "-")
      .replace(/^-+|-+$/g, "");

  const handleFromHref = (href) => {
    try {
      const match = new URL(href, window.location.href).pathname.match(
        /\/products\/([^/?#]+)/,
      );
      return match ? decodeURIComponent(match[1]).toLowerCase() : null;
    } catch (error) {
      return null;
    }
  };

  const refKey = (value) =>
    String(value || "")
      .replace(/\u00a0/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .replace(/\s*:$/, "")
      .toLowerCase();

  const track = (name, params) => {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: name }, params));
      if (typeof window.gtag === "function") window.gtag("event", name, params);
      if (typeof window.clarity === "function") window.clarity("event", name);
    } catch (error) {
      return;
    }
  };

  class FdArticleContent extends HTMLElement {
    connectedCallback() {
      if (this.initialized) {
        this.bindGlobal();
        this.bindReading();
        return;
      }
      this.initialized = true;
      this.onClick = (event) => this.handleClick(event);
      this.addEventListener("click", this.onClick, true);
      this.body = this.querySelector(".fd-article__body");
      if (this.body) {
        this.markSpacers();
        this.setHeadingIds();
        this.foldReferences();
        this.setupPreviews();
        this.bindGlobal();
      }
      this.setupReading();
      this.bindReading();
    }

    disconnectedCallback() {
      this.unbindGlobal();
      this.unbindReading();
    }

    /* ---------- blank editor paragraphs ---------- */

    markSpacers() {
      const brOnly =
        ":has(> br:only-child), :has(> :only-child > br:only-child)";
      const textAround = (node) =>
        [node.previousSibling, node.nextSibling].some(
          (side) => side && side.nodeType === 3 && !isBlank(side.textContent),
        );
      Array.from(this.body.children).forEach((node) => {
        if (node.tagName === "BR" && !textAround(node))
          node.classList.add("fd-article__spacer");
        if (node.tagName !== "P") return;
        if (!isBlank(node.textContent)) {
          try {
            if (node.matches(brOnly)) node.classList.add("fd-article__keep");
          } catch (error) {
            return;
          }
          return;
        }
        if (
          node.id ||
          node.querySelector(
            "img, picture, video, iframe, svg, embed, object, input, [id], [name]",
          )
        ) {
          node.classList.add("fd-article__keep");
          return;
        }
        node.classList.add("fd-article__spacer");
      });
    }

    /* ---------- contents card and reading progress ---------- */

    setupReading() {
      this.disclosure = this.querySelector(".fd-article__contents-disclosure");
      this.summary = this.disclosure
        ? this.disclosure.querySelector("summary")
        : null;
      this.links = Array.from(
        this.querySelectorAll(".fd-article__contents-link"),
      );
      this.headings = this.links.map((link) => {
        try {
          const target = document.getElementById(
            decodeURIComponent((link.getAttribute("href") || "").slice(1)),
          );
          return target && this.body && this.body.contains(target)
            ? target
            : null;
        } catch (error) {
          return null;
        }
      });
      this.bar = this.querySelector(".fd-article__progress-bar");
      this.percent = this.querySelector(".fd-article__progress-percent");
      const text = this.querySelector(".fd-article__progress-text");
      this.progressLabel = text ? text.dataset.progressLabel || "" : "";
      this.activeIndex = this.links.findIndex((link) =>
        link.classList.contains("fd-article__contents-link--active"),
      );
      this.wide = window.matchMedia("(min-width: 990px)");
      this.ticking = false;
      this.onReadingScroll = () => {
        if (this.ticking) return;
        this.ticking = true;
        window.requestAnimationFrame(() => {
          this.ticking = false;
          this.updateReading();
        });
      };
      this.onWideChange = () => {
        this.syncDisclosure();
        this.onReadingScroll();
      };
      this.onDisclosureToggle = () => {
        if (this.wide.matches && !this.disclosure.open)
          this.disclosure.open = true;
      };
      if (this.disclosure)
        this.disclosure.addEventListener("toggle", this.onDisclosureToggle);
      this.onBeforePrint = () => this.openForPrint();
      this.onAfterPrint = () => this.restoreAfterPrint();
      this.syncDisclosure();
      this.updateReading();
    }

    bindReading() {
      if (!this.wide || this.readingBound) return;
      this.readingBound = true;
      window.addEventListener("scroll", this.onReadingScroll, {
        passive: true,
      });
      window.addEventListener("resize", this.onReadingScroll, {
        passive: true,
      });
      if (this.wide.addEventListener)
        this.wide.addEventListener("change", this.onWideChange);
      else if (this.wide.addListener) this.wide.addListener(this.onWideChange);
      window.addEventListener("beforeprint", this.onBeforePrint);
      window.addEventListener("afterprint", this.onAfterPrint);
      this.syncDisclosure();
      this.onReadingScroll();
    }

    unbindReading() {
      if (!this.readingBound) return;
      this.readingBound = false;
      window.removeEventListener("scroll", this.onReadingScroll);
      window.removeEventListener("resize", this.onReadingScroll);
      if (this.wide.removeEventListener)
        this.wide.removeEventListener("change", this.onWideChange);
      else if (this.wide.removeListener)
        this.wide.removeListener(this.onWideChange);
      window.removeEventListener("beforeprint", this.onBeforePrint);
      window.removeEventListener("afterprint", this.onAfterPrint);
    }

    syncDisclosure() {
      if (!this.disclosure || !this.summary) return;
      if (this.wide.matches) {
        this.disclosure.open = true;
        this.summary.setAttribute("tabindex", "-1");
      } else {
        this.summary.removeAttribute("tabindex");
        if (this.wasWide) this.disclosure.open = false;
      }
      this.wasWide = this.wide.matches;
    }

    headerHeight() {
      if (document.querySelector(".mobile-navbar.header-scroll--hidden"))
        return 0;
      const value = parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue(
          "--header-height",
        ),
      );
      return Number.isFinite(value) ? value : 0;
    }

    updateReading() {
      if (!this.body) return;
      const hv = this.headerHeight();
      const rect = this.body.getBoundingClientRect();
      const scrollY = window.scrollY;
      const viewport = window.innerHeight;
      const atEnd =
        viewport + scrollY >= document.documentElement.scrollHeight - 2;
      const line = hv + 24 + 0.25 * viewport;
      let index = 0;
      this.headings.forEach((heading, i) => {
        if (heading && heading.getBoundingClientRect().top <= line) index = i;
      });

      const top = rect.top + scrollY;
      let progress =
        (scrollY + hv - top) / Math.max(1, rect.height - (viewport - hv));
      if (atEnd) progress = 1;
      progress = Math.min(1, Math.max(0, progress));

      if (this.bar) this.bar.style.transform = `scaleX(${progress.toFixed(4)})`;
      if (this.percent) {
        const label = ` • ${Math.round(progress * 100)}${this.progressLabel}`;
        if (this.percent.textContent !== label)
          this.percent.textContent = label;
        if (this.percent.hidden) this.percent.hidden = false;
      }

      if (!this.links.length || index === this.activeIndex) return;
      this.links.forEach((link, i) => {
        const active = i === index;
        link.classList.toggle("fd-article__contents-link--active", active);
        if (active) link.setAttribute("aria-current", "location");
        else link.removeAttribute("aria-current");
      });
      this.activeIndex = index;
    }

    followContentsLink(link, event) {
      if (!this.disclosure || this.wide.matches) return;
      const id = decodeURIComponent((link.getAttribute("href") || "").slice(1));
      const target = id ? document.getElementById(id) : null;
      if (!target) return;
      event.preventDefault();
      this.disclosure.open = false;
      target.scrollIntoView();
      if (window.history && window.history.pushState)
        window.history.pushState(null, "", `#${encodeURIComponent(id)}`);
    }

    /* ---------- share and print ---------- */

    async share(button) {
      const status = this.querySelector(".fd-article__share-status");
      if (status) status.textContent = "";
      const url = button.dataset.shareUrl || window.location.href.split("#")[0];
      const title = button.dataset.shareTitle || document.title;
      if (typeof navigator.share === "function") {
        try {
          await navigator.share({ title, url });
        } catch (error) {
          return;
        }
        return;
      }
      let copied = false;
      try {
        await navigator.clipboard.writeText(url);
        copied = true;
      } catch (error) {
        copied = this.copyFallback(url);
      }
      if (copied && status) {
        window.requestAnimationFrame(() => {
          status.textContent = status.dataset.copiedLabel || "";
        });
      }
    }

    copyFallback(text) {
      const area = document.createElement("textarea");
      area.value = text;
      area.setAttribute("readonly", "");
      area.className = "visually-hidden";
      document.body.appendChild(area);
      area.select();
      let ok = false;
      try {
        ok = document.execCommand("copy");
      } catch (error) {
        ok = false;
      }
      area.remove();
      return ok;
    }

    openForPrint() {
      this.printOpened = Array.from(
        this.querySelectorAll(
          "details.fd-article__refs:not([open]), details.fd-article__benefit-disclosure:not([open])",
        ),
      );
      this.printOpened.forEach((details) => {
        details.open = true;
      });
    }

    restoreAfterPrint() {
      (this.printOpened || []).forEach((details) => {
        details.open = false;
      });
      this.printOpened = [];
    }

    /* ---------- stable heading ids ---------- */

    setHeadingIds() {
      const links = Array.from(
        this.querySelectorAll(".fd-article__contents-link"),
      );
      const headings = Array.from(this.body.querySelectorAll("h2")).filter(
        (h) => !isBlank(h.textContent),
      );
      const used = new Set(headings.map((h) => h.id).filter(Boolean));
      links.forEach((link) =>
        used.add(
          decodeURIComponent((link.getAttribute("href") || "").slice(1)),
        ),
      );

      headings.forEach((heading, index) => {
        const link = links.find(
          (l) => l.dataset.headingIndex === String(index),
        );
        if (heading.id) {
          if (link) link.setAttribute("href", `#${heading.id}`);
          return;
        }
        let id = link
          ? decodeURIComponent((link.getAttribute("href") || "").slice(1))
          : "";
        if (!id) {
          const base = slugify(heading.textContent) || "section";
          id = base;
          for (let n = 2; used.has(id) || document.getElementById(id); n += 1)
            id = `${base}-${n}`;
          used.add(id);
        }
        heading.id = id;
      });

      if (window.location.hash) {
        let target = null;
        try {
          target = document.getElementById(
            decodeURIComponent(window.location.hash.slice(1)),
          );
        } catch (error) {
          target = null;
        }
        if (target && this.body.contains(target)) target.scrollIntoView();
      }
    }

    /* ---------- references ---------- */

    foldReferences() {
      const labels = String(this.body.dataset.referencesLabels || "")
        .split(",")
        .map(refKey)
        .filter(Boolean);
      if (!labels.length) return;

      const marker = Array.from(
        this.body.querySelectorAll(
          "h2, h3, h4, h5, h6, p, div, span, strong, b",
        ),
      )
        .filter((el) => labels.includes(refKey(el.textContent)))
        .pop();
      if (!marker) return;

      let block = marker;
      while (block.parentElement && block.parentElement !== this.body)
        block = block.parentElement;
      if (block.parentElement !== this.body) return;

      const rest = [];
      for (let node = block.nextSibling; node; node = node.nextSibling)
        rest.push(node);
      const HEADINGS = "h1, h2, h3, h4, h5, h6";
      if (
        rest.some(
          (node) =>
            node.nodeType === 1 &&
            (node.matches(HEADINGS) || node.querySelector(HEADINGS)),
        )
      )
        return;

      const key = refKey(marker.textContent);
      const details = document.createElement("details");
      details.className = "fd-article__refs";
      if (block.id) details.id = block.id;
      const summary = document.createElement("summary");
      summary.className = "fd-article__refs-summary";
      const text = document.createElement("span");
      text.textContent = key.charAt(0).toUpperCase() + key.slice(1);
      summary.appendChild(text);
      const iconTemplate = this.querySelector(
        ".fd-article__refs-icon-template",
      );
      if (iconTemplate) {
        const icon = document.createElement("span");
        icon.className = "fd-article__refs-icon";
        icon.setAttribute("aria-hidden", "true");
        icon.appendChild(iconTemplate.content.cloneNode(true));
        summary.appendChild(icon);
      }
      const list = document.createElement("div");
      list.className = "fd-article__refs-list";
      details.append(summary, list);
      this.body.insertBefore(details, block);

      if (refKey(block.textContent) === key) {
        block.remove();
      } else {
        marker.remove();
        while (
          block.firstChild &&
          (block.firstChild.nodeName === "BR" ||
            isBlank(block.firstChild.textContent))
        ) {
          block.firstChild.remove();
        }
        rest.unshift(block);
      }
      list.append(...rest);
    }

    /* ---------- product previews ---------- */

    setupPreviews() {
      const holder = this.querySelector(".fd-article__templates");
      const buttonTemplate =
        holder && holder.querySelector("template.fd-article__button-template");
      if (!holder || !buttonTemplate) return;

      const templates = new Map();
      holder
        .querySelectorAll("template.fd-article__card-template")
        .forEach((tpl) => {
          if (tpl.dataset.productHandle)
            templates.set(tpl.dataset.productHandle.toLowerCase(), tpl);
        });
      if (!templates.size) return;

      this.labels = {
        fine: holder.dataset.labelFine || "",
        coarse: holder.dataset.labelCoarse || holder.dataset.labelFine || "",
        hide: holder.dataset.labelHide || "",
      };
      const matchCta = holder.dataset.matchCta === "true";
      const sectionId = this.dataset.sectionId || "article";

      const anchors = Array.from(
        this.body.querySelectorAll('a[href*="/products/"]'),
      ).filter((a) => !a.closest(".fd-article__card"));
      this.mentions = [];
      anchors.forEach((anchor, index) => {
        const handle = handleFromHref(anchor.getAttribute("href"));
        const tpl = handle && templates.get(handle);
        if (!tpl) return;
        if (!isMention(anchor.textContent, namesFor(tpl), matchCta)) return;

        const cardId = `fd-article-card-${sectionId}-${index}`;
        const titleId = `fd-article-card-title-${sectionId}-${index}`;
        const button = buttonTemplate.content.firstElementChild.cloneNode(true);
        button.dataset.mentionIndex = String(index);
        button.dataset.productHandle = handle;
        button.setAttribute("aria-controls", cardId);
        const name = button.querySelector(".fd-article__preview-name");
        if (name) name.textContent = ` ${tpl.dataset.productTitle}`;

        const card = tpl.content
          .querySelector(".fd-article__card")
          .cloneNode(true);
        card.id = cardId;
        card.hidden = true;
        const title = card.querySelector(".fd-article__product-title");
        if (title) {
          title.id = titleId;
          card.setAttribute("aria-labelledby", titleId);
        }

        this.mentions.push({ anchor, button, card, handle, over: new Set() });
      });
      if (!this.mentions.length) return;

      this.active = null;
      this.suppressed = null;
      this.openTimer = null;
      this.closeTimer = null;
      this.media = window.matchMedia(FINE_QUERY);

      this.onMediaChange = () => this.placeAll();
      this.onKeydown = (event) => this.handleKeydown(event);
      this.onPointerdown = (event) => this.handleOutsidePointer(event);
      this.onResize = () => {
        if (
          this.active &&
          this.fine &&
          this.active.card.classList.contains("fd-article__card--floating")
        ) {
          this.setCardMode(this.active, "floating");
          if (
            !this.positionFloating(this.active) &&
            !this.positionClamped(this.active)
          ) {
            this.close(this.active, { restore: false });
          }
        }
      };
      this.mentions.forEach((m) => this.bindMention(m));
      this.placeAll();
    }

    bindGlobal() {
      if (!this.mentions || !this.mentions.length || this.bound) return;
      this.bound = true;
      if (this.media.addEventListener)
        this.media.addEventListener("change", this.onMediaChange);
      else if (this.media.addListener)
        this.media.addListener(this.onMediaChange);
      document.addEventListener("keydown", this.onKeydown);
      document.addEventListener("pointerdown", this.onPointerdown, true);
      window.addEventListener("resize", this.onResize);
      if (this.fine !== this.media.matches) this.placeAll();
    }

    unbindGlobal() {
      if (!this.bound) return;
      this.bound = false;
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
      if (this.media.removeEventListener)
        this.media.removeEventListener("change", this.onMediaChange);
      else if (this.media.removeListener)
        this.media.removeListener(this.onMediaChange);
      document.removeEventListener("keydown", this.onKeydown);
      document.removeEventListener("pointerdown", this.onPointerdown, true);
      window.removeEventListener("resize", this.onResize);
    }

    zone(m) {
      return [m.anchor, m.button, m.card];
    }

    focusInside(m) {
      const active = document.activeElement;
      return (
        !!active &&
        this.zone(m).some((el) => el === active || el.contains(active))
      );
    }

    bindMention(m) {
      this.zone(m).forEach((el) => {
        el.addEventListener("pointerenter", (event) => {
          if (!this.fine || event.pointerType === "touch") return;
          if (el === m.card && m.card.hidden) return;
          m.over.add(el);
          this.handleEnter(m);
        });
        el.addEventListener("pointerleave", (event) => {
          if (!this.fine || event.pointerType === "touch") return;
          if (el === m.card && !m.over.has(el)) return;
          m.over.delete(el);
          if (!m.over.size) this.handleLeave(m);
        });
        el.addEventListener("focusin", (event) => this.handleFocusIn(m, event));
        el.addEventListener("focusout", (event) =>
          this.handleFocusOut(m, event),
        );
      });

      m.button.addEventListener("click", () => {
        if (this.active === m) {
          if (this.fine && !m.pinned && !this.isInline(m)) {
            m.pinned = true;
            clearTimeout(this.closeTimer);
            return;
          }
          this.close(m, { restore: false });
        } else {
          this.suppressed = null;
          this.open(m, { explicit: true });
        }
      });

      m.card
        .querySelector(".fd-article__card-close")
        ?.addEventListener("click", () => this.dismiss(m));
    }

    placeAll() {
      this.fine = this.media.matches;
      if (this.active) this.close(this.active, { restore: false });
      this.suppressed = null;
      this.mentions.forEach((m) => {
        m.over.clear();
        m.button.classList.toggle(
          "fd-article__preview-button--block",
          !this.fine,
        );
        this.setLabel(m, false);
        if (this.fine) {
          m.anchor.after(m.button);
        } else {
          this.insertAfterBlock(m, m.button);
        }
        m.button.after(m.card);
        this.setCardMode(m, this.fine ? "floating" : "inline");
      });
    }

    blockOf(anchor) {
      const block =
        anchor.parentElement && anchor.parentElement.closest(BLOCK_SELECTOR);
      if (block && this.body.contains(block) && block !== this.body)
        return block;
      let top = anchor;
      while (top.parentElement && top.parentElement !== this.body)
        top = top.parentElement;
      return top;
    }

    insertAfterBlock(m, node) {
      const block = this.blockOf(m.anchor);
      if (block.tagName === "LI" || block.tagName === "TD") {
        block.append(node);
        return;
      }
      let ref = block;
      while (
        ref.nextElementSibling &&
        ref.nextElementSibling !== node &&
        ref.nextElementSibling.matches(
          ".fd-article__preview-button, .fd-article__card",
        )
      ) {
        ref = ref.nextElementSibling;
      }
      ref.after(node);
    }

    setLabel(m, open) {
      const label = m.button.querySelector(".fd-article__preview-label");
      if (!label) return;
      if (this.fine) label.textContent = this.labels.fine;
      else label.textContent = open ? this.labels.hide : this.labels.coarse;
    }

    setCardMode(m, mode) {
      m.card.classList.toggle(
        "fd-article__card--floating",
        mode === "floating",
      );
      m.card.classList.toggle("fd-article__card--inline", mode === "inline");
      ["max-height", "overflow-y", "width"].forEach((prop) =>
        m.card.style.removeProperty(prop),
      );
      if (mode === "inline") {
        m.card.style.removeProperty("top");
        m.card.style.removeProperty("left");
      }
    }

    isInline(m) {
      return m.card.classList.contains("fd-article__card--inline");
    }

    /* ---------- state machine ---------- */

    handleEnter(m) {
      if (this.active === m) {
        clearTimeout(this.closeTimer);
        return;
      }
      if (this.suppressed === m) return;
      clearTimeout(this.openTimer);
      this.openTimer = setTimeout(() => {
        if (m.over.size && this.suppressed !== m) this.open(m);
      }, OPEN_DELAY);
    }

    handleLeave(m) {
      clearTimeout(this.openTimer);
      if (this.suppressed === m) {
        this.suppressed = null;
        return;
      }
      if (this.focusInside(m) || m.pinned) return;
      if (this.active === m && !this.isInline(m)) {
        clearTimeout(this.closeTimer);
        this.closeTimer = setTimeout(() => {
          if (this.active === m && !m.over.size && !this.focusInside(m))
            this.close(m, { restore: false });
        }, CLOSE_DELAY);
      }
    }

    handleFocusIn(m, event) {
      if (!this.fine) return;
      if (event.target !== m.anchor && event.target !== m.button) return;
      if (!event.target.matches(":focus-visible")) return;
      if (this.suppressed === m || this.active === m) return;
      clearTimeout(this.openTimer);
      this.open(m);
    }

    handleFocusOut(m, event) {
      const next = event.relatedTarget;
      if (next && this.zone(m).some((el) => el === next || el.contains(next)))
        return;
      if (m.over.size) return;
      if (this.suppressed === m) {
        this.suppressed = null;
        return;
      }
      if (this.active === m && this.fine && !this.isInline(m))
        this.close(m, { restore: false });
    }

    handleKeydown(event) {
      if (event.key !== "Escape" && event.key !== "Esc") return;
      if (!this.active) return;
      this.dismiss(this.active);
    }

    handleOutsidePointer(event) {
      if (!this.fine || !this.active) return;
      const m = this.active;
      if (this.isInline(m)) return;
      if (this.zone(m).some((el) => el.contains(event.target))) return;
      if (this.focusInside(m)) return;
      this.close(m, { restore: false });
    }

    handleClick(event) {
      const target = event.target;
      if (!target || !target.closest) return;

      const contentsLink = target.closest(".fd-article__contents-link");
      if (contentsLink && this.contains(contentsLink)) {
        this.followContentsLink(contentsLink, event);
        return;
      }

      const print = target.closest(".fd-article__action--print");
      if (print && this.contains(print)) {
        window.print();
        return;
      }

      const shareButton = target.closest(".fd-article__action--share");
      if (shareButton && this.contains(shareButton)) {
        this.share(shareButton);
        return;
      }

      const mention = target.closest(".fd-article__mention");
      if (mention && this.contains(mention)) {
        const hit = target.closest(
          ".fd-article__mention-card a, .fd-article__mention-card .alt-product-card__atc",
        );
        if (hit && !hit.disabled) {
          track("article_product_click", {
            product_handle: mention.dataset.productHandle || "",
            placement: "module",
          });
        }
        return;
      }

      const cta = target.closest(".fd-article__product-cta");
      if (!cta || !this.contains(cta)) return;
      const product = cta.closest("[data-product-handle]");
      track("article_product_click", {
        product_handle: product ? product.dataset.productHandle : "",
        placement: "card",
      });
    }

    open(m, { explicit = false } = {}) {
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
      if (this.active && this.active !== m)
        this.close(this.active, { restore: false });
      if (this.fine) {
        if (!this.showFloating(m, explicit)) return;
      } else {
        this.setCardMode(m, "inline");
        m.card.hidden = false;
      }
      this.active = m;
      m.pinned = explicit;
      if (this.suppressed === m) this.suppressed = null;
      m.button.setAttribute("aria-expanded", "true");
      this.setLabel(m, true);
      track("article_preview_open", { product_handle: m.handle });
    }

    close(m, { restore }) {
      const hadFocus = m.card.contains(document.activeElement);
      m.card.hidden = true;
      m.over.delete(m.card);
      if (this.fine && m.card.previousElementSibling !== m.button)
        m.button.after(m.card);
      if (this.fine) this.setCardMode(m, "floating");
      m.button.setAttribute("aria-expanded", "false");
      this.setLabel(m, false);
      m.pinned = false;
      if (this.active === m) this.active = null;
      if (restore && hadFocus) m.button.focus();
    }

    dismiss(m) {
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
      const hadFocus = m.card.contains(document.activeElement);
      if (this.fine) this.suppressed = m;
      this.close(m, { restore: false });
      if (hadFocus) m.button.focus();
    }

    /* ---------- floating placement ---------- */

    headerBottom() {
      let bottom = 0;
      document
        .querySelectorAll(
          ".shopify-section-header, sticky-header, .header-wrapper-container, .shopify-section-group-header-group",
        )
        .forEach((el) => {
          const style = getComputedStyle(el);
          if (style.position !== "sticky" && style.position !== "fixed") return;
          const rect = el.getBoundingClientRect();
          if (rect.top <= 1 && rect.bottom > 0)
            bottom = Math.max(bottom, rect.bottom);
        });
      return bottom;
    }

    showFloating(m, explicit) {
      if (m.card.previousElementSibling !== m.button) m.button.after(m.card);
      this.setCardMode(m, "floating");
      m.card.style.left = "0px";
      m.card.style.top = "0px";
      m.card.hidden = false;
      if (this.positionFloating(m)) return true;
      if (explicit) {
        this.setCardMode(m, "inline");
        this.insertAfterBlock(m, m.card);
        return true;
      }
      if (this.positionClamped(m)) return true;
      m.card.hidden = true;
      this.setCardMode(m, "floating");
      return false;
    }

    positionClamped(m) {
      const card = m.card;
      const viewportWidth = document.documentElement.clientWidth;
      const viewportHeight = window.innerHeight;
      const rects = Array.from(m.anchor.getClientRects()).concat(
        Array.from(m.button.getClientRects()),
      );
      if (!rects.length) return false;
      const top = Math.min(...rects.map((r) => r.top));
      const bottom = Math.max(...rects.map((r) => r.bottom));
      const left = Math.min(...rects.map((r) => r.left));
      const below = viewportHeight - bottom - GAP - EDGE;
      const above = top - this.headerBottom() - GAP - EDGE;
      const space = Math.max(below, above);
      if (space < MIN_CLAMP) return false;

      card.style.width = `${Math.min(card.offsetWidth, viewportWidth - EDGE * 2)}px`;
      card.style.maxHeight = `${Math.floor(space)}px`;
      card.style.overflowY = "auto";
      const width = card.offsetWidth;
      const height = card.offsetHeight;
      const y = below >= above ? bottom + GAP : top - GAP - height;
      const x = Math.max(EDGE, Math.min(left, viewportWidth - EDGE - width));
      const origin = this.body.getBoundingClientRect();
      card.style.left = `${Math.round(x - origin.left)}px`;
      card.style.top = `${Math.round(y - origin.top)}px`;
      return true;
    }

    positionFloating(m) {
      const card = m.card;
      const viewportWidth = document.documentElement.clientWidth;
      const viewportHeight = window.innerHeight;
      const width = card.offsetWidth;
      const height = card.offsetHeight;
      if (viewportWidth < width + EDGE * 2) return false;

      const rects = Array.from(m.anchor.getClientRects()).concat(
        Array.from(m.button.getClientRects()),
      );
      if (!rects.length) return false;
      const top = Math.min(...rects.map((r) => r.top));
      const bottom = Math.max(...rects.map((r) => r.bottom));
      const left = Math.min(...rects.map((r) => r.left));

      let y;
      if (viewportHeight - bottom >= height + GAP) y = bottom + GAP;
      else if (top - this.headerBottom() >= height + GAP)
        y = top - GAP - height;
      else return false;

      const x = Math.max(EDGE, Math.min(left, viewportWidth - EDGE - width));
      const origin = this.body.getBoundingClientRect();
      card.style.left = `${Math.round(x - origin.left)}px`;
      card.style.top = `${Math.round(y - origin.top)}px`;
      return true;
    }
  }

  customElements.define("fd-article-content", FdArticleContent);
})();
